import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { getFlightDealIdsByYearMonth } from 'selectors/offerPage/offerPageSelectors'
import { pluralizeToString } from 'lib/string/pluralize'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import { LUXURY_PLUS } from 'loyalty/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

interface Props {
  fromPrice: number;
  duration: number;
  withFlights?: boolean;
  rooms: Array<App.RoomOccupants>;
  year?: string;
  month?: string;
  flightDealIdsByYearMonth?: Record<string, Record<string, string>>;
  displayMemberPrice: boolean;
}

function OfferCalendarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    flightDealIdsByYearMonth,
    year,
    month,
    rooms = EmptyArray,
    displayMemberPrice,
  } = props

  const averagePrice = Math.ceil(fromPrice / (rooms.length > 1 ? rooms.length : duration))

  const flightDealName = useMemo(() => {
    if (year && year in flightDealIdsByYearMonth) {
      const yearRecord = flightDealIdsByYearMonth[year]

      if (month) {
        if (month in yearRecord) {
          return yearRecord[month]
        }

        return undefined
      }

      return yearRecord[Object.keys(yearRecord)[0]]
    }

    return undefined
  }, [flightDealIdsByYearMonth, year, month])

  const unit = rooms.length > 1 ? 'room' : 'night'
  const roomCount = rooms.length

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
    >
      {!withFlights && <BodyText variant="small" colour="neutral-one" >
        {displayMemberPrice && <BodyText weight="bold" colour="loyalty" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString('room', roomCount)}, `}
        {pluralizeToString('night', duration)} from{' '}
        <b><FormatCurrency value={fromPrice} /></b> total{' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice && <BodyText weight="bold" colour="loyalty" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString('room', roomCount)}, `}
        {pluralizeToString('night', duration)} + round trip flights from{' '}
        <b><FormatCurrency value={fromPrice} />*</b> (2 adults){' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      {flightDealName && <CaptionTextBlock
        variant="medium"
        colour="neutral-two"
        startIcon={<SolidFireIcon colour="urgency" />}
      >
        {flightDealName}
      </CaptionTextBlock> }
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    flightDealIdsByYearMonth: getFlightDealIdsByYearMonth(appState, state).dealIds,
    displayMemberPrice: state.displayMemberPrice,
  }
})(OfferCalendarPriceBlurb)
